import { PencilIcon, TrashIcon, ChevronDoubleUpIcon, ChevronDoubleDownIcon } from "@heroicons/react/outline";
import React from "react";
import { TemplateIcon } from "@heroicons/react/outline";
import { useRef, useState } from "react";

import { API_AUTH_TYPES, API_ROUTES } from "../../../../../api/apiConstants";
import { useMutation, useQueryClient } from "react-query";
import { ApiRequest } from "../../../../../api";
import DoubleClickEdit from "../../../../../components/DoubleClickEdit";
import ItemMenu from "../../../../../components/menus/ItemMenu";
import { classNames } from "../../../../../helpers/classNames";
import { MdOutlineDragIndicator } from "react-icons/md";
import DraggableItem from "../../../../../components/drag-drop/DraggableItem";
import DropTarget from "../../../../../components/drag-drop/DropTarget";
import RichTextDisplay from "../../../../../components/textEditor/RichTextDisplay";
import QuillTextEditor from "../../../../../components/textEditor/Quill-Text-Editor";
import TaskTagList from "../../../../../components/task-tags/TaskTagList";

/**

 * @component
 * @param {Object} props - Component props
 * @param {Object} props.goal - Goal data { goal_id, chapter_id, content, ... }
 * @param {Object} props.queryKeys - Query key references for cache management
 * @param {string} props.queryKeys.goals - Goals list query key
 * @param {string} props.queryKeys.chapters - Chapters list query key
 * @param {string} props.queryKeys.search - Search results query key
 * @param {string} [props.queryKeys.active] - Active status query key
 * @param {boolean} [props.isMeetingMode=false] - Hides template creation if true
 * @returns {JSX.Element} Goal card with edit, menu, and drag controls
 *
 * @remarks
 * - Uses Quill editor for rich text content with draft=false
 * - Menu options conditional on meeting mode (template creation hidden)
 * - Drag-drop reordering updates goal order via PATCH
 * - Status updates archive goal instead of deleting
 * - Multiple query invalidations ensure cache sync
 * - Color-coded borders and backgrounds (white with gray-200 border)
 * - Flex layout with right-aligned menu/drag controls
 *
 * @example
 * <ChapterGoal
 *   goal={goalData}
 *   queryKeys={queryKeysObj}
 *   isMeetingMode={false}
 * />
 */
const ChapterGoal = ({ goal, queryKeys, isMeetingMode = false, startInEditMode = false }) => {
    const [showEdit, setShowEdit] = useState(startInEditMode);
    const quillEditorRef = useRef(null);

    const goalsApi = new ApiRequest(API_ROUTES.HOUSEHOLD_GOALS, API_AUTH_TYPES.ADVISOR);
    const queryClient = useQueryClient();


    const handleUpdateStatus = useMutation(async ({ new_status }) => {
        await goalsApi.patch({ endpoint: `update_status`, params: [goal.goal_id], body: { new_status } })

    }, {
        onSuccess: () => {

            queryClient.invalidateQueries(queryKeys.goals);
            queryClient.invalidateQueries(queryKeys.chapters);
            queryClient.invalidateQueries(queryKeys.search);

        }
    })


    const templateApi = new ApiRequest(API_ROUTES.FINANCIAL_STORY_TEMPLATES, API_AUTH_TYPES.ADVISOR);
    const { mutate: postTemplate } = useMutation(
        (body) => templateApi.post({ endpoint: "post_template", body: { content: goal.content, type: 'goal' } }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries('goal-templates')
            },
        }
    );

    const { mutate: reorderGoalToTop } = useMutation(
        (body) => goalsApi.patch({ endpoint: "reorder_to_top", body }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.goals);
            },
        }
    );

    const { mutate: reorderGoalToBottom } = useMutation(
        (body) => goalsApi.patch({ endpoint: "reorder_to_end", body }),
        {
            onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.goals);
            },
        }
    );

    const handleMoveToTop = () => {
        reorderGoalToTop({
            item_id: goal.goal_id,
            item_list_id: goal.chapter_id,
            dropped_over_list_id: goal.chapter_id,
            item_type: 'goal',
            household_id: goal.household_id,
        });
    };

    const handleMoveToBottom = () => {
        reorderGoalToBottom({
            item_id: goal.goal_id,
            item_list_id: goal.chapter_id,
            dropped_over_list_id: goal.chapter_id,
            item_type: 'goal',
            household_id: goal.household_id,
        });
    };

    const menuOptions = [
        {
            title: "Edit",
            action: () => setShowEdit(true),
            icon: PencilIcon,
        },
        {
            title: "Move to Top",
            action: handleMoveToTop,
            icon: ChevronDoubleUpIcon,
        },
        {
            title: "Move to Bottom",
            action: handleMoveToBottom,
            icon: ChevronDoubleDownIcon,
        },
        ...(!isMeetingMode ? [{
            title: "Create Template",
            action: postTemplate,
            icon: TemplateIcon,
        }] : []),
        {
            title: "Delete",
            action: () => handleUpdateStatus.mutate({ new_status: 'archived' }),
            icon: TrashIcon,
        },


    ];

    const { mutate: updateGoalOrder } = useMutation(
        (body) => goalsApi.patch({ endpoint: "update_order", body }),
        {
            onSuccess: ({ old_chapter_status, new_chapter_status }) => {
                queryClient.invalidateQueries(
                    queryKeys.goals
                );
                queryClient.invalidateQueries(queryKeys[old_chapter_status]);
                queryClient.invalidateQueries(queryKeys[new_chapter_status]);
            },
        }
    );

    const handleGoalReorder = (item) => {

        updateGoalOrder({
            item_id: item.content.props.goal?.goal_id,
            dropped_over_id: goal.goal_id,
            dropped_over_list_id: goal.chapter_id,
            item_list_id: item.content.props.goal?.chapter_id,
            item_type: 'goal'
        });
    }

    const handleSave = async () => {
        // Drain any keystrokes still sitting in Quill's 200ms autosave debounce
        // before invalidating. Otherwise the goals-list refetch triggered below
        // can race the in-flight save (or run while the debounce is cancelled
        // by unmount) and return pre-save content, wiping the advisor's last
        // typing from view mode.
        await quillEditorRef.current?.flush?.();
        queryClient.invalidateQueries(queryKeys.goals);
        queryClient.invalidateQueries(queryKeys.search);
        setShowEdit(false);
    };
    const options = {
        is_draft: false,
        route: "household_goals",
        search_params: { document_id: goal.goal_id },
    };

    const queryKey = `${goal.goal_id}`;

    return (
        <DropTarget accept="goal" onDrop={(item) => handleGoalReorder(item)} isActiveStyling={'pt-16 drop-target '} >

            <div className={classNames("text-gray-800  flex justify-between  border-2 bg-white  rounded-md")}>
                <div className=" px-3 py-4 w-full text-left">
                    <DoubleClickEdit
                        showEdit={showEdit}
                        setShowEdit={setShowEdit}
                        defaultComponent={<RichTextDisplay content={goal.content} thisClassName='markdown-notes' />}
                        editComponent={
                            <div className="w-full">
                                {goal.content ? (
                                    <QuillTextEditor
                                        ref={quillEditorRef}
                                        placeholder="Create new ongoing advice"
                                        options={options}
                                        queryKey={queryKey}
                                        listQueryKey={queryKeys.goals}
                                        autoFocus={startInEditMode}
                                    />
                                ) : (
                                    <>
                                        {/* <MarkdownNotes noteRaw={task.description} /> */}
                                        <p>Can't edit old note editor tasks</p>
                                    </>
                                )}
                            </div>
                        }
                        handleSubmitEdit={handleSave}
                        componentName="description"
                    />
                    {/* Tags */}
                    <TaskTagList
                        goalId={goal.goal_id}
                        type="goal"
                        tags={goal.tags || []}
                        isEditable={true}
                        onTagsChange={() => {
                            queryClient.invalidateQueries(queryKeys.goals);
                        }}
                    />
                </div>
                <div className="whitespace-nowrap px-3 py-4 text-sm text-gray-500  flex justify-end item-center space-x-5">



                    <div className="ml-2 flex flex-shrink-0 space-x-2 items-center ">
                        <DraggableItem
                            isDraggable={true}
                            type='goal'
                            item={{
                                type: 'goal',
                                content:
                                    <ChapterGoal goal={goal} queryKeys={queryKeys} />
                            }}
                        >
                            <MdOutlineDragIndicator className="h-6 w-6 text-gray-500" />
                        </DraggableItem>
                        <ItemMenu options={menuOptions} />
                    </div>


                </div>
            </div>
        </DropTarget>
    );
};

export default ChapterGoal; 